import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { CheckCircle2, ImageIcon, Printer } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { formatDateTime } from '@/lib/date'
import { reasonLabel, sectionName } from '@/lib/issueReasons'
import { useAdminDepartments } from '@/hooks/useAdminDepartments'
import type { IssueReason, IssueRow, Paginated } from '@/types'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import Pager from '@/components/ui/Pager'
import { Field, Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import Badge, { StateBadge } from '@/components/ui/Badge'
import { Table, Row, Cell, EmptyRow, actionsColumn, actionsCellClass } from '@/components/ui/Table'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'
import { cn } from '@/lib/cn'

// Laid out like the Orders All / Pending tabs. "Pending" is the open queue,
// the API's `open` state, and stays the default so the bell lands on it.
const statusTabs = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'cleared', label: 'Cleared' },
] as const

type IssueCounts = { open: number; cleared: number; resolved: number }
type IssueListResponse = Paginated<IssueRow> & { counts?: IssueCounts }

/* An open issue reads "pending" here to match the tab; the shared StateBadge
   keeps "open" for the reports that use it. */
function IssueStateBadge({ state }: { state: string }) {
  return state === 'open' ? <Badge tone="danger">pending</Badge> : <StateBadge state={state} />
}

const textareaClass =
  'w-full rounded-md border border-foreground/20 bg-background p-3 text-base sm:text-sm text-foreground placeholder:text-foreground/70 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/5 focus-visible:ring-offset-2'

/**
 * Every issue the floor (or a partner) flagged, newest first. "Open" is the
 * queue: nobody has acted on the job since the flag. Staff clear an issue by
 * starting or completing again, and the flagging or notified section can
 * resolve it from the job page. An admin can also resolve one here, with a
 * note, which is what the bell notification links to.
 */
export default function AdminIssues() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()

  // The tab lives in the URL like the Orders tabs, so Back and a report link
  // land on it. The other filters are seeded once from the URL and are plain
  // local state after that.
  const status = params.get('status') ?? 'open'
  const [departmentId, setDepartmentId] = useState(params.get('department_id') ?? '')
  const [reasonId, setReasonId] = useState(params.get('issue_reason_id') ?? '')
  const [search, setSearch] = useState(params.get('search') ?? '')
  const [from, setFrom] = useState(params.get('from') ?? '')
  const [to, setTo] = useState(params.get('to') ?? '')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<IssueRow | null>(null)
  const [note, setNote] = useState('')

  const filters = { status, department_id: departmentId, issue_reason_id: reasonId, search, from, to }

  // keepPreviousData holds the last list (and the tab counts) on screen while
  // a new tab or filter loads, instead of flashing the loader each click.
  const { data, isLoading, isPlaceholderData } = useQuery({
    queryKey: ['admin-issues', filters, page],
    queryFn: async () =>
      (
        await api.get<IssueListResponse>('/admin/issues', {
          params: {
            page,
            status,
            ...(departmentId ? { department_id: departmentId } : {}),
            ...(reasonId ? { issue_reason_id: reasonId } : {}),
            ...(search ? { search } : {}),
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
          },
        })
      ).data,
    placeholderData: keepPreviousData,
  })

  const departments = useAdminDepartments()

  const reasons = useQuery({
    queryKey: ['admin-issue-reasons'],
    queryFn: async () => (await api.get<{ data: IssueReason[] }>('/admin/issue-reasons')).data.data,
  })

  const resolve = useMutation({
    mutationFn: async (id: number) =>
      (await api.post<{ message: string }>(`/admin/issues/${id}/resolve`, { note: note.trim() || undefined })).data,
    onSuccess: (res) => {
      for (const key of ['admin-issues', 'admin-issue-count', 'report-issues', 'report-overview', 'admin-dash', 'admin-jobs', 'orders-page']) {
        queryClient.invalidateQueries({ queryKey: [key] })
      }
      toast.success(res.message)
      setSelected(null)
      setNote('')
    },
    onError: (err) => toast.error('Resolve failed', errorMessage(err)),
  })

  const filter = (set: (value: string) => void) => (value: string) => {
    set(value)
    setPage(1)
  }

  const showTab = (value: string) => {
    const next = new URLSearchParams(params)
    if (value === 'open') next.delete('status')
    else next.set('status', value)
    setParams(next, { replace: true })
    setPage(1)
  }

  const open = (issue: IssueRow) => {
    setSelected(issue)
    setNote('')
  }

  const counts = data?.counts

  return (
    <div>
      <PageHeader
        title="Flagged issues"
        subtitle="Problems the floor raised on jobs - pending ones are waiting for someone to act"
      />

      <div className="mb-3 flex flex-wrap gap-2" role="tablist" aria-label="Issue list">
        {statusTabs.map((tab) => {
          const count = tab.value === 'all' ? undefined : counts?.[tab.value]
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={status === tab.value}
              onClick={() => showTab(tab.value)}
              className={cn(
                'inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition',
                status === tab.value
                  ? 'border-primary/60 bg-primary/20 text-primary'
                  : 'border-line bg-surface text-foreground/70 hover:bg-foreground/5',
              )}
            >
              {tab.label}
              {count !== undefined && (
                <span
                  className={cn(
                    'rounded-full px-1.5 text-2xs font-bold',
                    tab.value === 'open' && count > 0 ? 'bg-pending text-white' : 'bg-foreground/10 text-foreground/60',
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-line bg-surface p-3">
        <Input
          placeholder="Search job code..."
          value={search}
          onChange={(e) => filter(setSearch)(e.target.value)}
          className="w-full min-w-0 sm:flex-1"
        />
        <Select
          value={departmentId}
          onChange={filter(setDepartmentId)}
          placeholder="All sections"
          options={[{ value: '', label: 'All sections' }, ...(departments.data?.departments ?? []).map((d) => ({ value: String(d.id), label: d.name }))]}
          className="w-full sm:w-44"
        />
        <Select
          value={reasonId}
          onChange={filter(setReasonId)}
          placeholder="All reasons"
          options={[
            { value: '', label: 'All reasons' },
            ...(reasons.data ?? []).map((r) => {
              const label = reasonLabel(r.name, sectionName(r.department_id, departments.data?.departments))
              return { value: String(r.id), label: r.active ? label : `${label} (inactive)` }
            }),
            { value: 'other', label: 'Other (typed)' },
          ]}
          className="w-full sm:w-52"
        />
        <Input
          type="date"
          title="Flagged from"
          aria-label="Flagged from"
          value={from}
          max={to || undefined}
          onChange={(e) => filter(setFrom)(e.target.value)}
          className="min-w-0 flex-1 sm:w-40 sm:flex-none"
        />
        <Input
          type="date"
          title="Flagged to"
          aria-label="Flagged to"
          value={to}
          min={from || undefined}
          onChange={(e) => filter(setTo)(e.target.value)}
          className="min-w-0 flex-1 sm:w-40 sm:flex-none"
        />
      </div>

      {isLoading ? (
        <LogoLoader />
      ) : (
        <div className={cn('transition-opacity', isPlaceholderData && 'pointer-events-none opacity-60')} aria-busy={isPlaceholderData}>
          <Table columns={['Job code', 'Order', 'Customer', 'Section', 'Flagged by', 'Reason', 'Flagged at', 'Status', actionsColumn]}>
            {(data?.data.length ?? 0) === 0 && (
              <EmptyRow colSpan={9}>{status === 'open' ? 'No pending issues - nothing is waiting on you.' : 'No issues match these filters.'}</EmptyRow>
            )}
            {data?.data.map((issue) => (
              <Row key={issue.id} onClick={() => open(issue)}>
                <Cell className="font-mono text-xs font-bold">
                  <Link to={`/admin/jobs/${issue.job_id}/sheet?from=issues`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="hover:underline">
                    {issue.job_code}
                  </Link>
                </Cell>
                <Cell>
                  <Link to={`/admin/orders/${issue.order_id}`} onClick={(e) => e.stopPropagation()} className="hover:underline">
                    {issue.order_number ?? '-'}
                  </Link>
                </Cell>
                <Cell>{issue.customer ?? '-'}</Cell>
                <Cell>{issue.department ?? '-'}</Cell>
                <Cell>{issue.staff}</Cell>
                <Cell className={cn(issue.status === 'open' && 'font-semibold text-status-danger')}>
                  <span className="flex max-w-64 items-center gap-1.5">
                    <span className="truncate" title={issue.reason ?? ''}>
                      {issue.reason ?? '-'}
                    </span>
                    {issue.photo_path && <ImageIcon className="size-3.5 shrink-0 text-muted" aria-label="Has a photo" />}
                  </span>
                </Cell>
                <Cell className="whitespace-nowrap text-xs text-muted">{formatDateTime(issue.at)}</Cell>
                <Cell>
                  <IssueStateBadge state={issue.status} />
                </Cell>
                <Cell className={actionsCellClass}>
                  {issue.status === 'open' && (
                    <Button
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation()
                        open(issue)
                      }}
                    >
                      <CheckCircle2 className="size-4" /> Resolve
                    </Button>
                  )}
                </Cell>
              </Row>
            ))}
          </Table>
          <Pager meta={data?.meta} page={page} onPage={setPage} />
        </div>
      )}

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected ? `Issue on ${selected.job_code}` : ''}
        footer={
          selected && (
            <>
              <Link to={`/admin/jobs/${selected.job_id}/sheet?from=issues`} target="_blank" rel="noreferrer">
                <Button variant="outline">
                  <Printer className="size-4" /> Job sheet
                </Button>
              </Link>
              {selected.status === 'open' ? (
                <Button loading={resolve.isPending} onClick={() => resolve.mutate(selected.id)}>
                  <CheckCircle2 className="size-4" /> Mark resolved
                </Button>
              ) : (
                <Button variant="outline" onClick={() => setSelected(null)}>
                  Close
                </Button>
              )}
            </>
          )
        }
      >
        {selected && (
          <div className="space-y-4 text-sm">
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
              <dt className="text-muted">Status</dt>
              <dd>
                <IssueStateBadge state={selected.status} />
              </dd>
              <dt className="text-muted">Order</dt>
              <dd>
                <Link to={`/admin/orders/${selected.order_id}`} className="font-semibold text-primary hover:underline">
                  {selected.order_number ?? '-'}
                </Link>
                {selected.customer && <span className="text-muted"> - {selected.customer}</span>}
              </dd>
              <dt className="text-muted">Section</dt>
              <dd>{selected.department ?? '-'}</dd>
              <dt className="text-muted">Flagged by</dt>
              <dd>
                {selected.staff} <span className="text-muted">on {formatDateTime(selected.at)}</span>
              </dd>
              <dt className="text-muted">Job now</dt>
              <dd>
                <StateBadge state={selected.job_status} />
              </dd>
            </dl>

            <div className="rounded-lg border border-danger/40 bg-danger/5 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-status-danger">Reason</p>
              <p className="mt-1 whitespace-pre-wrap">{selected.reason ?? '-'}</p>
              {selected.issue_reason && (
                <p className="mt-1 text-xs text-muted">Preset: {selected.issue_reason}</p>
              )}
              {(selected.notified_departments?.length ?? 0) > 0 && (
                <p className="mt-1 text-xs text-muted">
                  Previous sections notified: {selected.notified_departments!.map((d) => d.name).join(', ')}
                </p>
              )}
              {selected.photo_path && (
                <a href={selected.photo_path} target="_blank" rel="noreferrer" className="mt-2 block">
                  <img
                    src={selected.photo_path}
                    alt="Photo of the problem"
                    className="max-h-64 w-full rounded-lg border border-foreground/15 object-contain"
                  />
                  <span className="mt-1 block text-xs text-muted">Photo from the floor - tap to open full size.</span>
                </a>
              )}
            </div>

            {selected.status === 'resolved' && (
              <div className="rounded-lg border border-success/40 bg-success/5 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-status-success">Resolved</p>
                <p className="mt-1">
                  {selected.resolved_by ?? 'An admin'} <span className="text-muted">on {formatDateTime(selected.resolved_at)}</span>
                </p>
                {selected.resolution_note && <p className="mt-1 whitespace-pre-wrap">{selected.resolution_note}</p>}
              </div>
            )}

            {selected.status === 'cleared' && (
              <p className="text-xs text-muted">
                The section started or completed this job again after the flag, so it no longer blocks production.
              </p>
            )}

            {selected.status === 'open' && (
              <Field label="Resolution note (optional)">
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="What was decided, e.g. customer sent a new artwork file"
                  className={textareaClass}
                />
                <p className="text-xs text-muted">
                  Marking it resolved puts the job back to {selected.department ?? 'its section'} so the floor can carry on.
                </p>
              </Field>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
